import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL, formatCurrency, formatDate } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
    CreditCard,
    Crown,
    Zap,
    Building2,
    Check,
    X,
    Clock,
    AlertTriangle,
    ArrowRight,
    Loader2,
    Receipt,
    CalendarDays,
    Users,
    FolderKanban,
    HardDrive,
    Sparkles,
    Shield,
    Headphones,
} from "lucide-react";

const PLAN_ICONS = {
    basic: Zap,
    pro: Crown,
    enterprise: Building2,
};

const PLAN_COLORS = {
    basic: "border-blue-500/30 bg-blue-500/5",
    pro: "border-violet-500/30 bg-violet-500/5",
    enterprise: "border-amber-500/30 bg-amber-500/5",
};

const PLAN_BADGE_COLORS = {
    basic: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    pro: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
    enterprise: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

const STATUS_CONFIG = {
    active: { label: "Actif", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", icon: Check },
    trialing: { label: "Essai gratuit", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: Clock },
    past_due: { label: "Paiement en retard", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400", icon: AlertTriangle },
    cancelled: { label: "Annule", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: X },
    cancelled_active: { label: "Annule (actif jusqu'a fin de periode)", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400", icon: Clock },
    trial_expired: { label: "Essai expire", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: X },
    expired: { label: "Expire", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: X },
    no_subscription: { label: "Aucun abonnement", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400", icon: CreditCard },
};

export const SubscriptionPage = () => {
    const { token } = useAuth();
    const [plans, setPlans] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [subCheck, setSubCheck] = useState(null);
    const [planDetails, setPlanDetails] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [billingCycle, setBillingCycle] = useState("monthly");
    const [cancelling, setCancelling] = useState(false);
    const [startingTrial, setStartingTrial] = useState(false);

    const fetchData = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const results = await Promise.allSettled([
            fetch(`${API_URL}/subscription/plans`).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/subscription/status`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/subscription/history`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
        ]);
        if (results[0].status === "fulfilled") setPlans(results[0].value);
        if (results[1].status === "fulfilled") {
            setSubscription(results[1].value.subscription);
            setSubCheck(results[1].value.check);
            setPlanDetails(results[1].value.plan_details);
        }
        if (results[2].status === "fulfilled") setHistory(results[2].value);
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCheckout = async (planId) => {
        setCheckoutLoading(planId);
        try {
            const res = await fetch(`${API_URL}/subscription/checkout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    plan_id: planId,
                    billing_cycle: billingCycle,
                    origin_url: window.location.origin,
                }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Checkout failed");
            }
            const data = await res.json();
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setCheckoutLoading(null);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm("Etes-vous sur de vouloir annuler votre abonnement ? Vous garderez l'acces jusqu'a la fin de la periode en cours.")) return;
        setCancelling(true);
        try {
            const res = await fetch(`${API_URL}/subscription/cancel`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Cancellation failed");
            }
            toast.success("Abonnement annule. Acces maintenu jusqu'a la fin de la periode.");
            fetchData();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setCancelling(false);
        }
    };

    const handleStartTrial = async (planId) => {
        setStartingTrial(true);
        try {
            const res = await fetch(`${API_URL}/subscription/start-trial`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ plan_id: planId }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Failed to start trial");
            }
            toast.success("Essai gratuit de 14 jours active !");
            fetchData();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setStartingTrial(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const currentStatus = subCheck?.status || "no_subscription";
    const statusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.no_subscription;
    const StatusIcon = statusCfg.icon;
    const currentPlanId = subscription?.plan_id;
    const hasActiveSubscription = subCheck?.active && currentStatus !== "no_subscription";

    return (
        <div className="space-y-8 animate-fade-in" data-testid="subscription-page">
            {/* Header */}
            <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                    Abonnement
                </h1>
                <p className="text-muted-foreground mt-1">
                    Gerez votre plan et vos paiements
                </p>
            </div>

            {/* Current Status Card */}
            <Card data-testid="subscription-status-card">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <CreditCard className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle>Statut de l'abonnement</CardTitle>
                                <CardDescription>
                                    {subscription ? `Plan ${planDetails?.name || currentPlanId}` : "Aucun abonnement actif"}
                                </CardDescription>
                            </div>
                        </div>
                        <Badge className={statusCfg.color} data-testid="subscription-status-badge">
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusCfg.label}
                        </Badge>
                    </div>
                </CardHeader>
                {subscription && (
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-xs text-muted-foreground">Plan</p>
                                <p className="font-semibold text-sm">{planDetails?.name || currentPlanId}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Cycle</p>
                                <p className="font-semibold text-sm">
                                    {subscription.billing_cycle === "yearly" ? "Annuel" : "Mensuel"}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Prochaine echeance</p>
                                <p className="font-semibold text-sm">
                                    {formatDate(subscription.current_period_end)}
                                </p>
                            </div>
                            {subCheck?.days_left !== undefined && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Jours restants</p>
                                    <p className="font-semibold text-sm">{subCheck.days_left} jours</p>
                                </div>
                            )}
                        </div>
                        {currentStatus !== "cancelled" && currentStatus !== "cancelled_active" && hasActiveSubscription && (
                            <div className="mt-4 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                    className="text-destructive hover:text-destructive"
                                    data-testid="cancel-subscription-btn"
                                >
                                    {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                                    Annuler l'abonnement
                                </Button>
                            </div>
                        )}
                    </CardContent>
                )}
            </Card>

            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center gap-4">
                <span className={`text-sm font-medium ${billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground"}`}>
                    Mensuel
                </span>
                <button
                    onClick={() => setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")}
                    className={`relative w-14 h-7 rounded-full transition-colors ${billingCycle === "yearly" ? "bg-primary" : "bg-muted"}`}
                    data-testid="billing-cycle-toggle"
                >
                    <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${billingCycle === "yearly" ? "translate-x-7" : "translate-x-0.5"}`} />
                </button>
                <span className={`text-sm font-medium ${billingCycle === "yearly" ? "text-foreground" : "text-muted-foreground"}`}>
                    Annuel
                    <Badge variant="outline" className="ml-2 text-green-600 border-green-300 text-xs">
                        -17%
                    </Badge>
                </span>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
                {plans.map((plan) => {
                    const Icon = PLAN_ICONS[plan.plan_id] || Zap;
                    const isCurrentPlan = currentPlanId === plan.plan_id;
                    const price = billingCycle === "yearly" ? plan.price_yearly : plan.price_monthly;
                    const monthlyEquiv = billingCycle === "yearly" ? (plan.price_yearly / 12).toFixed(0) : null;

                    return (
                        <Card
                            key={plan.plan_id}
                            className={`relative transition-all duration-200 hover:shadow-lg ${isCurrentPlan ? "ring-2 ring-primary" : ""} ${PLAN_COLORS[plan.plan_id] || ""}`}
                            data-testid={`plan-card-${plan.plan_id}`}
                        >
                            {isCurrentPlan && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                    <Badge className="bg-primary text-primary-foreground">Plan actuel</Badge>
                                </div>
                            )}
                            {plan.plan_id === "pro" && !isCurrentPlan && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                    <Badge className="bg-violet-600 text-white">Populaire</Badge>
                                </div>
                            )}
                            <CardHeader className="text-center pb-2">
                                <div className="mx-auto w-12 h-12 rounded-xl bg-background flex items-center justify-center mb-3 shadow-sm">
                                    <Icon className="w-6 h-6 text-primary" />
                                </div>
                                <CardTitle className="text-xl">{plan.name}</CardTitle>
                                <CardDescription className="text-xs">{plan.description}</CardDescription>
                            </CardHeader>
                            <CardContent className="text-center space-y-6">
                                <div>
                                    <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-4xl font-bold text-foreground">
                                            {formatCurrency(price)}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {billingCycle === "yearly" ? `/an` : `/mois`}
                                        {monthlyEquiv && ` (${monthlyEquiv} EUR/mois)`}
                                    </p>
                                </div>

                                <Separator />

                                {/* Limits */}
                                <div className="space-y-3 text-sm text-left">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-muted-foreground" />
                                        <span>{plan.limits?.users === -1 ? "Illimite" : plan.limits?.users} utilisateurs</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FolderKanban className="w-4 h-4 text-muted-foreground" />
                                        <span>{plan.limits?.projects === -1 ? "Illimite" : plan.limits?.projects} projets</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <HardDrive className="w-4 h-4 text-muted-foreground" />
                                        <span>{plan.limits?.storage_gb === -1 ? "Illimite" : `${plan.limits?.storage_gb} Go`} stockage</span>
                                    </div>
                                </div>

                                <Separator />

                                {/* Features */}
                                <div className="space-y-2 text-sm text-left">
                                    {plan.features?.map((f, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                            <span>{f}</span>
                                        </div>
                                    ))}
                                    {plan.plan_id === "enterprise" && (
                                        <div className="flex items-center gap-2 text-amber-600">
                                            <Headphones className="w-4 h-4 flex-shrink-0" />
                                            <span className="font-medium">Support prioritaire</span>
                                        </div>
                                    )}
                                </div>

                                {/* Action Button */}
                                <div className="pt-2">
                                    {isCurrentPlan ? (
                                        <Button variant="outline" className="w-full" disabled>
                                            <Check className="w-4 h-4 mr-2" />
                                            Plan actuel
                                        </Button>
                                    ) : !hasActiveSubscription && currentStatus === "no_subscription" ? (
                                        <div className="space-y-2">
                                            <Button
                                                className="w-full"
                                                onClick={() => handleStartTrial(plan.plan_id)}
                                                disabled={startingTrial}
                                                variant={plan.plan_id === "pro" ? "default" : "outline"}
                                                data-testid={`trial-btn-${plan.plan_id}`}
                                            >
                                                {startingTrial ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
                                                Essai gratuit 14 jours
                                            </Button>
                                            <Button
                                                className="w-full"
                                                variant="secondary"
                                                onClick={() => handleCheckout(plan.plan_id)}
                                                disabled={checkoutLoading === plan.plan_id}
                                                data-testid={`checkout-btn-${plan.plan_id}`}
                                            >
                                                {checkoutLoading === plan.plan_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                                                S'abonner
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            className="w-full"
                                            onClick={() => handleCheckout(plan.plan_id)}
                                            disabled={checkoutLoading === plan.plan_id}
                                            variant={plan.plan_id === "pro" ? "default" : "outline"}
                                            data-testid={`checkout-btn-${plan.plan_id}`}
                                        >
                                            {checkoutLoading === plan.plan_id ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <ArrowRight className="w-4 h-4 mr-2" />
                                            )}
                                            {currentPlanId && plans.findIndex(p => p.plan_id === plan.plan_id) > plans.findIndex(p => p.plan_id === currentPlanId)
                                                ? "Upgrader" : "Changer de plan"}
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Payment History */}
            {history.length > 0 && (
                <Card data-testid="payment-history-card">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <Receipt className="w-5 h-5 text-muted-foreground" />
                            <div>
                                <CardTitle>Historique des paiements</CardTitle>
                                <CardDescription>Vos transactions recentes</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {history.map((txn) => (
                                <div key={txn.transaction_id} className="flex items-center justify-between py-3 border-b last:border-0">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-2 h-2 rounded-full ${txn.payment_status === "paid" ? "bg-green-500" : txn.payment_status === "initiated" ? "bg-yellow-500" : "bg-red-500"}`} />
                                        <div>
                                            <p className="text-sm font-medium">
                                                Plan {txn.plan_id} - {txn.billing_cycle === "yearly" ? "Annuel" : "Mensuel"}
                                            </p>
                                            <p className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-semibold">{formatCurrency(txn.amount, txn.currency?.toUpperCase())}</p>
                                        <Badge variant="outline" className="text-xs">
                                            {txn.payment_status === "paid" ? "Paye" : txn.payment_status === "initiated" ? "En cours" : txn.payment_status}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
